import { FC, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { getGuidesList } from '../service';
import { Page } from '../types';
import { useGetPage } from '../hooks/useGetPage';
import GuideAvatar from './GuideAvatar';
import {
  getGuideAdditionalPhotoUrls,
  getGuideAvatarUrl,
  getGuideBodyHtml,
  getGuideDestinations,
  getGuideName,
  getGuideProfile,
  getGuideSlug,
} from './guidesHelpers';

const GuideDetailsPage: FC = () => {
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const { guideUrl } = useParams<{ guideUrl: string }>();
  const [guide, setGuide] = useState<Page | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const galleryRef = useRef<HTMLDivElement>(null);

  useGetPage('');

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const pages = await getGuidesList();
      const slug = (guideUrl || '').replace(/^\/+/, '');
      const found =
        (pages || []).find((page) => getGuideSlug(page).toLowerCase() === slug.toLowerCase()) ||
        null;
      setGuide(found);
      if (found) {
        document.title = getGuideName(found) || translate('guides.title');
      } else {
        document.title = translate('guides.title');
      }
      setIsLoading(false);
    })();
  }, [guideUrl]);

  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return (
      <div className="guide-details">
        <div className="guide-details-loading">{translate('guides.loading')}</div>
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="guide-details">
        <div className="guide-details-empty">{translate('guides.empty')}</div>
        <Link className="guide-details-back" to="/guides">
          {translate('guides.back')}
        </Link>
      </div>
    );
  }

  const profile = getGuideProfile(guide);
  const name = getGuideName(guide);
  const destinations = getGuideDestinations(guide);
  const bodyHtml = getGuideBodyHtml(guide);
  const avatar = getGuideAvatarUrl(guide);
  const photos = getGuideAdditionalPhotoUrls(guide);

  const details = [
    { label: translate('guides.experience'), value: profile?.experience },
    { label: translate('guides.education'), value: profile?.education },
    { label: translate('guides.specialization'), value: profile?.specialization },
    { label: translate('guides.languages'), value: profile?.languages },
    { label: translate('guides.tours'), value: profile?.tours },
    { label: translate('guides.reviews'), value: profile?.reviews },
  ].filter((item) => !!item.value);

  return (
    <div className="guide-details">
      <div className="guide-details-hero">
        <GuideAvatar className="guide-details-avatar" src={avatar} alt={name} />
        <div className="guide-details-info">
          <h1 className="guide-details-name">{name}</h1>

          {!!destinations && (
            <div className="guide-details-destinations">
              <strong>{translate('guides.destinations')}</strong> {destinations}
            </div>
          )}

          {!!profile?.bio && <p className="guide-details-body">{profile.bio}</p>}

          {!!details.length && (
            <div className="guide-details-meta">
              {details.map((item) => (
                <p className="guide-details-meta-row" key={item.label}>
                  <strong>{item.label}</strong> {item.value}
                </p>
              ))}
            </div>
          )}

          {!!bodyHtml && (
            <div
              className="guide-details-body"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GuideDetailsPage;
